import { isAxiosError } from "axios";
import { ValidationError as YupValidationError } from "yup";
import { ZodError } from "zod";

import {
  getHttpStatus,
  getServerErrorMessage,
  isHttpError,
} from "./http/errors";

export const unexpectedErrorMessage = "An unexpected error occurred";

/**
 * Normalizes an unknown caught value into a string message suitable for use
 * in analytics events and log lines. Prefers `Error.message` when available;
 * passes non-empty strings through as-is; otherwise falls back to the provided default.
 */
export function toErrorMessage(
  err: unknown,
  fallback = unexpectedErrorMessage,
): string {
  if (err instanceof Error) return err.message.trim() || fallback;
  if (typeof err === "string") return err.trim() || fallback;
  return fallback;
}

export type ErrorMessageResolver = string | ((error: unknown) => string | null);

export interface DisplayErrorMessageOptions {
  fallback?: string | null;
  notFoundMessage?: ErrorMessageResolver;
  statusMessages?: Partial<Record<number, ErrorMessageResolver>>;
}

function resolveMessageOverride(
  override: ErrorMessageResolver | undefined,
  error: unknown,
): string | null {
  if (!override) return null;
  const message = typeof override === "function" ? override(error) : override;
  return typeof message === "string" && message.trim() ? message.trim() : null;
}

function firstNonEmpty(messages: unknown[]): string | null {
  for (const message of messages) {
    if (typeof message === "string" && message.trim()) {
      return message.trim();
    }
  }

  return null;
}

function getValidationErrorMessage(error: unknown): string | null {
  if (error instanceof ZodError) {
    return firstNonEmpty(error.issues.map((issue) => issue.message));
  }

  if (error instanceof YupValidationError) {
    return firstNonEmpty(
      error.inner.length > 0
        ? error.inner.map((innerError) => innerError.message)
        : [error.message],
    );
  }

  return null;
}

/**
 * Normalizes errors for user-facing UI copy. Server-provided API messages win
 * for expected HTTP failures, while 5xx responses intentionally use generic
 * copy so implementation details are not exposed to patients or staff.
 */
export function getDisplayErrorMessage(
  error: unknown,
  {
    fallback = unexpectedErrorMessage,
    notFoundMessage,
    statusMessages,
  }: DisplayErrorMessageOptions = {},
): string | null {
  if (error == null) return null;

  if (typeof error === "string") {
    return error.trim() || fallback;
  }

  const status = getHttpStatus(error);
  const statusOverride = status
    ? resolveMessageOverride(statusMessages?.[status], error)
    : null;

  if (statusOverride) return statusOverride;

  if (status === 404) {
    const message = resolveMessageOverride(notFoundMessage, error);
    if (message) return message;
  }

  if (status && status >= 500) {
    return fallback;
  }

  const validationMessage = getValidationErrorMessage(error);
  if (validationMessage) return validationMessage;

  const serverMessage = getServerErrorMessage(error);
  if (serverMessage) return serverMessage;

  if (isHttpError(error)) return fallback;

  if (isAxiosError(error)) {
    return error.message.trim() || fallback;
  }

  if (error instanceof Error) {
    return error.message.trim() || fallback;
  }

  return fallback;
}
